import { X } from "lucide-react";
import { useState } from "react";
import { Language, dictionary } from "../../../utils/dictionary";
import {
  useFacebookSDK,
  EmbeddedSignupResult,
} from "../../../hooks/useFacebookSDK";
import { INTEGRATIONS_UI_CONFIG } from "../config/integrations.config";

interface ModalWhatsAppIntegrationProps {
  setModalOpen: (open: boolean) => void;
  handleOAuthComplete: (data: EmbeddedSignupResult) => void;
  loading: boolean;
  language: Language;
}

const ModalWhatsAppIntegration = ({
  setModalOpen,
  handleOAuthComplete,
  loading,
  language,
}: ModalWhatsAppIntegrationProps) => {
  const whatsappLogo = INTEGRATIONS_UI_CONFIG.whatsapp.logoSrc;
  const { sdkReady, launchEmbeddedSignup } = useFacebookSDK();
  const [sdkLoading, setSdkLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConnect = async () => {
    setError(null);
    setSdkLoading(true);
    try {
      const result = await launchEmbeddedSignup();
      handleOAuthComplete(result);
    } catch (err: any) {
      console.error("Embedded signup failed:", err);
      setError(err?.message || "Error connecting WhatsApp");
    } finally {
      setSdkLoading(false);
    }
  };

  const isLoading = loading || sdkLoading;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center p-4">
      <div className="bg-white p-4 md:p-6 rounded-lg shadow-xl w-full max-w-md relative">
        <button
          className="absolute top-3 md:top-4 right-3 md:right-4 z-10"
          onClick={() => setModalOpen(false)}
        >
          <X className="w-5 h-5 md:w-6 md:h-6" />
        </button>

        <div className="flex flex-col items-center text-center">
          {/* WhatsApp icon */}
          <div className="w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center mb-4">
            <img
              src={whatsappLogo}
              alt="WhatsApp"
              className="w-9 h-9"
            />
          </div>

          <h2 className="text-xl md:text-2xl font-bold mb-2">
            {dictionary["whatsapp-connect-title"][language]}
          </h2>

          <p className="text-sm text-gray-600 mb-6">
            {dictionary["whatsapp-connect-description"][language]}
          </p>

          {error && (
            <div className="w-full mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            className="w-full px-4 py-3 bg-[#25D366] text-white rounded-lg hover:bg-[#1da851] transition-colors font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleConnect}
            disabled={!sdkReady || isLoading}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <img
                  src={whatsappLogo}
                  alt=""
                  className="w-5 h-5"
                />
                {dictionary["whatsapp-connect-button"][language]}
              </>
            )}
          </button>

          <button
            className="w-full mt-3 px-4 py-2 text-sm md:text-base bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition-colors"
            onClick={() => setModalOpen(false)}
          >
            {dictionary["cancel"][language]}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalWhatsAppIntegration;
